import clsx from 'clsx';
import React from 'react';

import { useResizeObserver } from '@gitbook/util-react-hooks';

import type { DeviceResolution } from './devices';
import { SitePreviewPlaceholder } from './SitePreviewPlaceholder';

/**
 * An iframe with a controlled resolution.
 */
export function Iframe(
    props: {
        src: string | null;

        /**
         * Screen resolution to use within the iframe.
         */
        scaleResolution?: DeviceResolution;

        /**
         * If true, the user can interact with the iframe.
         */
        interactive?: boolean;
    } & React.ComponentProps<'div'>
) {
    const { src, scaleResolution, interactive = false, ...rest } = props;

    const [loading, setLoading] = React.useState(true);

    const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
    const containerRef = useResizeObserver((entry) => {
        setContainerWidth(entry.contentRect.width);
    });

    return (
        <div
            ref={containerRef}
            className={clsx('relative overflow-hidden', rest.className)}
            style={
                scaleResolution
                    ? { aspectRatio: scaleResolution.width / scaleResolution.height }
                    : {}
            }
        >
            {containerWidth ? (
                <div
                    className="relative"
                    style={
                        scaleResolution
                            ? {
                                  width: scaleResolution.width,
                                  height: scaleResolution.height,
                                  transform: `scale(${containerWidth / scaleResolution.width})`,
                                  transformOrigin: 'top left',
                              }
                            : {
                                  width: '100%',
                                  height: '100%',
                              }
                    }
                >
                    {src ? (
                        <iframe
                            key={src}
                            title="Site Preview"
                            sandbox="allow-same-origin allow-scripts"
                            src={src}
                            width="100%"
                            height="100%"
                            style={{ border: 0, margin: 0 }}
                            onLoad={() => setLoading(false)}
                            // @ts-expect-error
                            inert={interactive ? undefined : 'true'}
                        />
                    ) : null}
                    <div
                        className={clsx(
                            'absolute inset-0 transition',
                            !loading && 'opacity-0',
                            interactive && 'pointer-events-none'
                        )}
                    >
                        <SitePreviewPlaceholder className="absolute inset-0" aria-busy />
                        <div
                            className="shimmer absolute inset-0"
                            style={{
                                backgroundImage:
                                    'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0) 100%)',
                            }}
                            aria-busy={!loading}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}
